import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, CheckCircle, DollarSign, Loader2 } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const STATUS_BADGE = {
  pending:    'bg-amber-50 text-amber-700',
  processing: 'bg-sky-50 text-sky-700',
  paid:       'bg-emerald-50 text-emerald-700',
  failed:     'bg-red-50 text-red-600',
};

const PayoutDetailsForm = ({ profile, onSaved }) => {
  const [method, setMethod] = useState(profile?.payout_method || 'paypal');
  const [email, setEmail] = useState(profile?.payout_email || '');
  const [bankName, setBankName] = useState(profile?.payout_bank_name || '');
  const [accountHolder, setAccountHolder] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (method === 'paypal' && !email.trim()) { setError('PayPal email is required.'); return; }
    if (method === 'bank_transfer' && !bankName.trim()) { setError('Bank name is required.'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/creator/payout-details`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payout_method: method,
          payout_email: email || null,
          payout_bank_name: bankName || null,
          payout_account_holder: accountHolder || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save.');
      }
      onSaved({ payout_method: method, payout_email: email, payout_bank_name: bankName });
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
      <h2 className="font-semibold text-gray-900 mb-1">Payout Details</h2>
      <p className="text-sm text-gray-500 mb-5">We need your payout details before we can send you money.</p>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Payout method</label>
          <div className="flex gap-3">
            {[{ value: 'paypal', label: 'PayPal' }, { value: 'bank_transfer', label: 'Bank transfer' }].map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="method"
                  value={opt.value}
                  checked={method === opt.value}
                  onChange={() => setMethod(opt.value)}
                  className="text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {method === 'paypal' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">PayPal email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        )}

        {method === 'bank_transfer' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Bank name</label>
              <input
                type="text"
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                placeholder="e.g. Chase Bank"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Account holder name</label>
              <input
                type="text"
                value={accountHolder}
                onChange={e => setAccountHolder(e.target.value)}
                placeholder="Full legal name"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : <><CheckCircle className="w-4 h-4" /> Save payout details</>}
        </button>
      </div>
    </div>
  );
};

const CreatorPayouts = () => {
  const [profile, setProfile] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState('');
  const LIMIT = 20;

  const load = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };

    try {
      const [prof, pouts, summ] = await Promise.all([
        fetch(`${API_URL}/api/creator/profile`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/creator/payouts?page=${page}&limit=${LIMIT}`, { headers: h }).then(r => r.ok ? r.json() : { items: [], total: 0 }),
        fetch(`${API_URL}/api/creator/earnings/summary`, { headers: h }).then(r => r.ok ? r.json() : null),
      ]);
      setProfile(prof);
      setPayouts(pouts.items || []);
      setTotal(pouts.total || 0);
      setSummary(summ);
    } catch {
      // leave stale data
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleRequestPayout = async () => {
    setRequestError('');
    setRequestSuccess('');
    setRequesting(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/creator/payout/request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = typeof data.detail === 'string'
          ? data.detail
          : data.detail?.message || `Error ${r.status}`;
        throw new Error(msg);
      }
      const amount = ((data.total_amount_cents || 0) / 100).toFixed(2);
      setRequestSuccess(`Payout of $${amount} requested successfully! Processing within 3–5 business days.`);
      await load();
    } catch (err) {
      setRequestError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const availableCents = summary?.available_earnings?.total_cents || 0;
  const needsPayoutDetails = !profile?.payout_method;

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">Payouts</h1>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Available balance + request payout */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-gray-500">Available for payout</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  ${(availableCents / 100).toFixed(2)}
                </p>
                {summary?.pending_earnings?.total_cents > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    + ${(summary.pending_earnings.total_cents / 100).toFixed(2)} pending (refund window)
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={handleRequestPayout}
                  disabled={requesting || availableCents === 0 || needsPayoutDetails}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={needsPayoutDetails ? 'Set up payout details first' : availableCents === 0 ? 'No available balance' : undefined}
                >
                  {requesting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Requesting…</>
                    : <><Wallet className="w-4 h-4" /> Request Payout</>
                  }
                </button>
                {needsPayoutDetails && (
                  <p className="text-xs text-amber-600">Set up payout details below first</p>
                )}
              </div>
            </div>

            {requestSuccess && (
              <div className="mt-4 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">
                {requestSuccess}
              </div>
            )}
            {requestError && (
              <div className="mt-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                {requestError}
              </div>
            )}
          </div>

          {needsPayoutDetails && (
            <PayoutDetailsForm
              profile={profile}
              onSaved={(details) => setProfile(p => ({ ...p, ...details }))}
            />
          )}

          {!needsPayoutDetails && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">Payout method</p>
                <p className="text-sm text-gray-500 mt-0.5 capitalize">
                  {profile.payout_method === 'paypal'
                    ? `PayPal · ${profile.payout_email}`
                    : `Bank transfer · ${profile.payout_bank_name}`}
                </p>
              </div>
              <button
                onClick={() => setProfile(p => ({ ...p, payout_method: null }))}
                className="text-sm text-violet-600 font-semibold hover:text-violet-700"
              >
                Change
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <p className="font-semibold text-gray-900">Payout history</p>
              {total > 0 && <span className="text-xs text-gray-400">{total} payouts</span>}
            </div>

            {payouts.length === 0 ? (
              <div className="text-center py-14">
                <Wallet className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No payouts yet</p>
                <p className="text-xs text-gray-400 mt-1">Once you request a payout, it will appear here.</p>
              </div>
            ) : (
              <>
                <div className="hidden md:grid grid-cols-4 gap-4 px-5 py-3 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  <div>Date</div>
                  <div className="text-right">Amount</div>
                  <div className="text-center">Status</div>
                  <div className="text-right">Method</div>
                </div>
                <div className="divide-y divide-gray-50">
                  {payouts.map((p, i) => (
                    <div key={p.id || i} className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4 px-5 py-4 items-center">
                      <p className="text-sm text-gray-700">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 md:text-right">
                        ${((p.total_amount_cents || 0) / 100).toFixed(2)}
                      </p>
                      <div className="flex md:justify-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-500'}`}>
                          {p.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 md:text-right capitalize">{p.payout_method || '—'}</p>
                    </div>
                  ))}
                </div>

                {total > LIMIT && (
                  <div className="px-5 py-4 border-t border-gray-50 flex justify-end gap-2">
                    {page > 1 && (
                      <button onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50">
                        Previous
                      </button>
                    )}
                    {payouts.length === LIMIT && (
                      <button onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700">
                        Next
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </CreatorLayout>
  );
};

export default CreatorPayouts;
